// import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react"
import {
  Box,
  Container,
  Grid2,
  Paper,
  Stack,
  Typography
} from '@mui/material'
import {
  format,
  isWithinInterval,
  setDate,
  startOfMonth,
  subDays,
  subMonths
} from 'date-fns/fp'
import log from 'loglevel'
import { UserOutletContext, EnumPaymentGmoPgStatus } from '../../types'
import { isEmpty, isNil, map } from 'lodash/fp'
import { useEffect, useState } from 'react'
import { requestGetInvoices } from '../../functions/common'
import { Loading } from '../Loading'
import { ServerError } from '../ServerError'
import { Link, useOutletContext } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { pickMostPriorirtyLog } from '../PaymentEdit/functions'

log.setLevel(log.levels.DEBUG)

const dispLogsMonth = 15
const dispRepaymentStatDate = 1
const dispRepaymentEndDate = 16
const dispCurrntLog = (
  logs: Record<
    string,
    {
      effectiveDate: string
      amount: number
      status: EnumPaymentGmoPgStatus
    }[]
  >
) => {
  if (isEmpty(logs)) return <></>
  const currentLogs = logs[format('yyyy-MM')(Date.now()) as keyof typeof logs]
  if (isNil(currentLogs)) return <></>
  const invoice = pickMostPriorirtyLog(currentLogs)
  if (isNil(invoice)) return <></>
  const props = {
    log: invoice,
    latest: true
  }
  return (
    <>
      <Typography
        align="left"
        variant="h4"
        sx={{
          borderLeft: 20,
          borderBottom: 1,
          borderColor: 'orange',
          paddingLeft: 1
        }}>
        当月のお支払い
      </Typography>
      <Box justifyItems={'center'}>
        <LogComponent {...props} />
      </Box>
    </>
  )
}

const dispPastLog = (
  logs: Record<
    string,
    {
      effectiveDate: string
      amount: number
      status: EnumPaymentGmoPgStatus
    }[]
  >,
  latest?: boolean
) => {
  const dispLogs: Array<
    | {
        effectiveDate: string
        amount: number
        status: EnumPaymentGmoPgStatus
      }
    | undefined
  > = []
  for (let index = 1; index <= dispLogsMonth; index++) {
    const month = format('yyyy-MM')(subMonths(index)(Date.now()))
    if (!isEmpty(logs[month])) {
      const picked = pickMostPriorirtyLog(logs[month])
      dispLogs.push(picked)
    } else {
      dispLogs.push(undefined)
    }
  }
  return map(
    (
      log:
        | {
            effectiveDate: string
            amount: number
            status: EnumPaymentGmoPgStatus
          }
        | undefined
    ) => {
      return <LogComponent log={log} />
    }
  )(dispLogs)
}

const LogComponent = (props: {
  log:
    | {
        effectiveDate: string
        amount: number
        status: EnumPaymentGmoPgStatus
      }
    | undefined
  latest?: boolean
}) => {
  const { log, latest } = props
  if (isNil(log)) return <></>
  const { status, effectiveDate, amount } = log
  return (
    <Box width={'80%'} mb={1}>
      <Paper sx={{ padding: 4 }}>
        
        <Typography variant="h6" mb={1}>
          {format('yyyy年MM月分')(effectiveDate)}
        </Typography>
        {latest === true ? (
          <Typography variant="caption" mb={1}>
            お支払い状況:{' '}
            {status === EnumPaymentGmoPgStatus.Processed
              ? '決済完了'
              : status === EnumPaymentGmoPgStatus.Processing
              ? '決済処理中'
              : status === EnumPaymentGmoPgStatus.Error
              ? <Typography component={'span'} variant='caption' color="red">決済失敗</Typography>
              : '-'}
          </Typography>
        ) : (
          <></>
        )}
        <Typography variant="caption" display={'block'}>
          決済日: {format('yyyy年MM月dd日')(effectiveDate)}
        </Typography>
        <Typography variant="caption" display={'block'}>
          決済額: {amount}円
        </Typography>
      </Paper>
    </Box>
  )
}

const NothingLogs = () => {
  return (
    <Box>
      <Container maxWidth="xl">
        <Grid2 container>
          <Grid2 size={{ sm: 2 }} />
          <Grid2 size={{ lg: 8, sm: 8 }} spacing={10}>
            <Typography variant="h4" align="center">
              支払い履歴が見つかりませんでした。
            </Typography>
          </Grid2>
        </Grid2>
      </Container>
    </Box>
  )
}

export const PaymentLog = () => {
  const { user } = useAuth0()
  const { subscription } = useOutletContext<UserOutletContext>()
  const [invoices, setInvoices] = useState<
    Record<
      string,
      {
        effectiveDate: string
        amount: number
        status: EnumPaymentGmoPgStatus
      }[]
    >
  >({})
  const [loading, setLoading] = useState<boolean>(true)
  const [paymentError, setPaymentError] = useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)
  useEffect(() => {
    if (!isNil(user?.sub) && !isNil(subscription)) {
      const getInvoice = async () => {
        const invoices = await requestGetInvoices(
          subscription.zuoraAccountNo,
          user?.sub as string
        )
        if (isNil(invoices)) {
          log.error('[PaymentEdit] cannot get invoices')
          setHasError(true)
          return undefined
        }
        const current = invoices[format('yyyy-MM')(Date.now())]
        if (
          pickMostPriorirtyLog(current)?.status ===
          EnumPaymentGmoPgStatus.Error
        ) {
          setPaymentError(true)
        }
        setInvoices(invoices)
        setLoading(false)
      }
      getInvoice()
    }
  }, [])
  return loading ? (
    <Loading />
  ) : hasError ? (
    <ServerError />
  ) : isEmpty(invoices) ? (
    <NothingLogs />
  ) : (
    <Box>
      <Container sx={{ pt: 5 }} >
        <Box component="form" sx={{ mt: 1 }} maxWidth={"90%"}>
          <Stack spacing={3}>
            {dispCurrntLog(invoices)}
            {paymentError ? (
              <>
                <Typography variant="caption" mt={5} maxWidth={"90%"}>
                  ※お支払いが「決済失敗」の場合、当月のサービスはご利用いただけません。
                  <br />
                  サービス再開をご希望される場合は、ご登録いただいたクレジットカードがご利用可能か確認いただき、<Link to={'/user/paymentEdit'}>こちらから再登録</Link>してください。
                  <br />
                  翌月1日のお支払いが完了すると、翌月からサービスを再開いたします。
                  <br />
                </Typography>
              </>
            ) : (
              <></>
            )}
            {paymentError &&
            isWithinInterval({
              start: subDays(dispRepaymentStatDate)(startOfMonth(Date.now())),
              end: setDate(dispRepaymentEndDate)(Date.now())
            })(Date.now()) ? (
              <>
                <Typography variant="caption" mt={5} maxWidth={"90%"}>
                  ※有効なカードが登録された状態で、
                  <Link to={'/user/PaymentEdit'}>こちらから再決済</Link>
                  を行っていただくことで、当月の途中からサービスをご利用再開いただくことも可能です。
                  <br />
                  ・再決済は当月15日まで実施可能です。
                  <br />
                  ・再決済でのお支払い完了後、サービスがご利用可能となるまで3営業日程度かかる場合がございます。予めご了承ください。
                  <br />
                </Typography>
              </>
            ) : (
              <></>
            )}
            <Typography
              align="left"
              variant="h4"
              sx={{
                borderLeft: 20,
                borderBottom: 1,
                borderColor: 'orange',
                paddingLeft: 1
              }}>
              過去のお支払い履歴
            </Typography>
            <Box justifyItems={'center'}>{dispPastLog(invoices)}</Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}
